// frontend/src/pages/yard/YardInventoryHistory.tsx
//
// Yard Inventory History — a Sunday→Saturday week grid (yards down the left,
// one column per day) for supervisors to spot days a yard's inventory walk was
// missed. A day is "Done" when the yard had >=1 yardevent check-in ('I') that
// day; the cell shows the time of the latest check-in and who did it. Missing
// days are flagged. Clicking a Done cell opens the Yard Report for that yard.
//
// Data source rationale lives in the backend (YardService.get_inventory_history_weekly):
// yardevent is the append-only log with full daily history; yardinv.lastseendatetime
// is current-state and cannot reconstruct past days.
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import yardService from '../../services/yard.service';
import { YardInventoryHistoryResponse } from '../../types/yard';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKS_OF_HISTORY = 52; // how far back the dropdown / prev button allow

// Local YYYY-MM-DD key (NOT toISOString, which is UTC and can shift the day).
function toKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Sunday 00:00 of the week containing d.
function startOfSunday(d: Date): Date {
  const out = new Date(d);
  out.setDate(out.getDate() - out.getDay());
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function formatTime(iso: string): string {
  const dt = new Date(iso); // naive (server-local) ISO parses as local time
  if (isNaN(dt.getTime())) return '';
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

export default function YardInventoryHistory() {
  const navigate = useNavigate();

  const todayKey = useMemo(() => toKey(new Date()), []);
  const currentWeekStart = useMemo(() => startOfSunday(new Date()), []);
  const minWeekStart = useMemo(
    () => addDays(currentWeekStart, -WEEKS_OF_HISTORY * 7),
    [currentWeekStart],
  );

  const [weekStart, setWeekStart] = useState<Date>(currentWeekStart);
  const [data, setData] = useState<YardInventoryHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  // Current week is the most recent; navigation only goes backward through history.
  const canGoForward = weekStart.getTime() < currentWeekStart.getTime();
  const canGoBack = weekStart.getTime() > minWeekStart.getTime();

  const weekOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    for (let i = 0; i <= WEEKS_OF_HISTORY; i++) {
      const start = addDays(currentWeekStart, -i * 7);
      opts.push({
        value: toKey(start),
        label: i === 0 ? 'This Week' : `Week of ${start.getMonth() + 1}/${start.getDate()}`,
      });
    }
    return opts;
  }, [currentWeekStart]);

  const navigateWeek = (direction: number) => {
    const next = addDays(weekStart, direction * 7);
    if (next.getTime() > currentWeekStart.getTime()) return; // no future
    if (next.getTime() < minWeekStart.getTime()) return;     // past the floor
    setWeekStart(next);
  };

  const reqIdRef = useRef(0);
  useEffect(() => {
    const controller = new AbortController();
    const reqId = ++reqIdRef.current;
    const startDate = toKey(weekStart);
    const endDate = toKey(addDays(weekStart, 6));

    setLoading(true);
    setError(null);
    yardService
      .getInventoryHistoryWeekly(startDate, endDate, controller.signal)
      .then((res) => {
        if (reqId === reqIdRef.current) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || reqId !== reqIdRef.current) return;
        setError(err?.response?.data?.detail || 'Failed to load yard inventory history.');
        setLoading(false);
      });

    return () => controller.abort();
  }, [weekStart]);

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h1 className="text-xl font-semibold text-body">
            Yard Inventory History
          </h1>
          <p className="text-sm text-muted">
            Whether each yard's inventory was completed, by day. Page back to audit previous weeks.
          </p>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700" />
            Done
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-red-50 dark:bg-red-950 border border-red-300 dark:border-red-800" />
            Not Done
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" />
            Upcoming
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-sm border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto">
        <table className="w-full border-collapse table-fixed">
          <thead className="bg-slate-600 dark:bg-slate-700 text-white sticky top-0 z-10">
            <tr>
              <th className="px-2 py-1 text-center font-semibold border-r border-slate-500 w-72 text-xs">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => navigateWeek(-1)}
                    disabled={!canGoBack}
                    aria-label="Previous week"
                    className={`p-1 rounded ${canGoBack ? 'hover:bg-slate-500' : 'opacity-40 cursor-not-allowed'}`}
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <select
                    value={toKey(weekStart)}
                    onChange={(e) => setWeekStart(new Date(e.target.value + 'T00:00:00'))}
                    className="bg-slate-500 text-white border-none rounded px-2 py-1 text-sm font-semibold cursor-pointer hover:bg-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    {weekOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => navigateWeek(1)}
                    disabled={!canGoForward}
                    aria-label="Next week"
                    className={`p-1 rounded ${canGoForward ? 'hover:bg-slate-500' : 'opacity-40 cursor-not-allowed'}`}
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </th>
              {weekDays.map((day, i) => {
                const isToday = toKey(day) === todayKey;
                return (
                  <th
                    key={i}
                    className={`px-1 py-1 text-center text-xs font-semibold ${i < 6 ? 'border-r border-slate-500' : ''} ${isToday ? 'bg-slate-800' : ''}`}
                  >
                    <div className="flex flex-col items-center">
                      <span>{DAY_NAMES[day.getDay()]}</span>
                      <span className="text-sm font-bold">{day.getMonth() + 1}/{day.getDate()}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && data && data.yards.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted">
                  No yards found.
                </td>
              </tr>
            )}
            {!loading && data && data.yards.map((yard) => (
              <tr key={yard.yardrecno} className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-2 py-1 text-sm font-medium text-body border-r border-gray-200 dark:border-gray-700 w-72 truncate" title={yard.yard_name}>
                  {yard.yard_name}
                </td>
                {weekDays.map((day, i) => {
                  const key = toKey(day);
                  const dayData = yard.days[key];
                  const isFuture = key > todayKey;
                  const borderCls = i < 6 ? 'border-r border-gray-200 dark:border-gray-700' : '';

                  if (dayData) {
                    return (
                      <td
                        key={i}
                        onClick={() => navigate(`/yard/reports?yardrecno=${yard.yardrecno}`)}
                        title={`${yard.days[key].event_count} check-ins · ${new Date(dayData.last_event).toLocaleString()}${dayData.verified_by ? ' · ' + dayData.verified_by : ''}`}
                        className={`px-1 py-1 text-center align-middle cursor-pointer bg-green-100 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-900/70 ${borderCls}`}
                      >
                        <div className="flex flex-col items-center leading-tight">
                          <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span className="text-xs font-semibold text-green-800 dark:text-green-200">{formatTime(dayData.last_event)}</span>
                          {dayData.verified_by && (
                            <span className="text-[0.625rem] text-green-700 dark:text-green-300 truncate max-w-[90px]" title={dayData.verified_by}>
                              {dayData.verified_by}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  }

                  if (isFuture) {
                    return (
                      <td key={i} className={`px-1 py-1 text-center text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-gray-800/40 ${borderCls}`}>
                        —
                      </td>
                    );
                  }

                  return (
                    <td key={i} className={`px-1 py-1 text-center text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 ${borderCls}`}>
                      Not Done
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
